import React, { ReactElement, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SortDirection } from '@lanterds/types';
import { formatDate, formatEndpointAddress } from 'src/util/formatters';
import { ParcelSearchContext } from '../ParcelSearchContext';
import ParcelCode from './ParcelCode';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ColumnDef, Header } from '@tanstack/react-table';
import { Box, Button, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { getCapitalized } from 'src/util/helpers';
import { useSnackbar, VariantType } from 'notistack';
import './ResultsGrid.scss';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';

dayjs.extend(utc);

export default function ResultsGrid({
    shouldCondenseForm,
    updateFormSizeHandler,
}: {
    shouldCondenseForm: boolean;
    updateFormSizeHandler: (containerRefElement?: HTMLDivElement | null) => Promise<void>;
}) {
    const {
        parcels,
        lastResponseLessThanPage,
        shouldShowResultsScrollError,
        shouldShowLoading,
        parcelsAreLoading,
        warningText,
        handleSortClick,
        sortColumn,
        sortDirection,
        snackbarKeys,
        setSnackbarKeys,
    } = useContext(ParcelSearchContext);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [selectedRows, setSelectedRows] = useState({});
    const [sortOrder, setSortOrder] = useState<any>({
        id: sortColumn ? `${sortColumn[0].toLowerCase()}${sortColumn.slice(1, sortColumn.length + 1)}` : '',
        desc: sortDirection === 'desc',
    });

    const toolTipMessages = {
        dates: 'All times are local unless otherwise specified',
        parcelCode: 'Click the parcel code to see the parcel details',
    };

    const TABLE_CONTAINER_HEIGHT = 63;

    useEffect(() => {
        let message = '';
        let variant: VariantType = 'default';

        if (warningText) {
            message = warningText;
            variant = 'warning';
        } else if (lastResponseLessThanPage) {
            message = 'No more results for this search';
            variant = 'info';
        } else if (!shouldShowLoading && parcelsAreLoading) {
            message = 'Loading more results...';
            variant = 'loading';
        } else if (shouldShowResultsScrollError) {
            message = 'There has been an error fetching more results';
            variant = 'error';
        }

        if (message) {
            const snackbarKey = enqueueSnackbar(message, {
                variant,
                autoHideDuration: null,
                preventDuplicate: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });

            if (!snackbarKeys) {
                setSnackbarKeys({ [variant]: [snackbarKey] });
            } else {
                const variantSnackbarKeys = snackbarKeys[variant];
                if (variantSnackbarKeys) {
                    setSnackbarKeys({ ...snackbarKeys, [variant]: [...variantSnackbarKeys, snackbarKey] });
                } else {
                    setSnackbarKeys({ ...snackbarKeys, [variant]: [snackbarKey] });
                }
            }
        }
    }, [lastResponseLessThanPage, shouldShowLoading, parcelsAreLoading, shouldShowResultsScrollError, warningText]);

    const onSortChange = (header: Header<any, unknown>) => {
        const { column } = header;
        const { columnDef } = column;
        const { meta } = columnDef ?? {};

        let direction = SortDirection.ASCENDING;

        if (column.id.toLowerCase() === sortColumn?.toLowerCase() && sortDirection === SortDirection.ASCENDING) {
            direction = SortDirection.DESCENDING;
        }

        const sortName = (meta as any)?.sortingOptionName;

        if (sortName) {
            if (sortName.toLowerCase() === column.id.toLowerCase()) {
                const newOrder = direction === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
                setSortOrder({
                    column: sortName,
                    order: newOrder,
                });
                header.column.toggleSorting(direction === 'desc');
            } else {
                setSortOrder({
                    column: sortName,
                    order: SortDirection.DESCENDING,
                });
                header.column.toggleSorting(true);
            }
        }

        closeSnackbar();

        handleSortClick(getCapitalized(column.id), direction);
    };

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'customer',
            header: 'Customer',
            size: 80,
            meta: {
                sortingOptionName: 'customer',
                innerHtml: ({ row, getValue }: CellContext<any, unknown>) => {
                    const { sourceSystemTooltip } = row.original;
                    return (
                        <Tooltip arrow title={sourceSystemTooltip} placement="top">
                            {getValue() as ReactElement}
                        </Tooltip>
                    );
                },
            },
        },
        {
            accessorKey: 'endpoint',
            header: 'Endpoint',
            size: 200,
            meta: {
                sortingOptionName: 'endpoint',
            },
        },
        {
            accessorKey: 'containerType',
            header: 'Parcel Type',
            size: 80,
            meta: {
                sortingOptionName: 'containerType',
            },
        },
        {
            accessorKey: 'distress',
            header: 'Distress',
            size: 80,
            meta: {
                sortingOptionName: 'distress',
            },
        },
        {
            accessorKey: 'possessionDate',
            header: 'Possession Date',
            size: 100,
            meta: {
                sortingOptionName: 'possessionDate',
                headerHtml: ({ header }, displayText) => {
                    return (
                        <>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.dates} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </>
                    );
                },
            },
        },
        {
            accessorKey: 'deliveryDate',
            header: 'Delivery Date',
            size: 100,
            meta: {
                sortingOptionName: 'deliveryDate',
                headerHtml: ({ header }, displayText) => {
                    return (
                        <>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.dates} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </>
                    );
                },
            },
        },
        {
            accessorKey: 'parcelCode',
            header: 'Parcel Code',
            size: 100,
            meta: {
                sortingOptionName: 'parcelCode',
                innerHtml: ({ row }: CellContext<any, unknown>) => {
                    const { parcelCode } = row.original;
                    return (
                        <Box className="parcel-code-cell">
                            <ParcelCode parcelInfo={parcelCode} />
                        </Box>
                    );
                },
                headerHtml: ({ header }, displayText) => {
                    return (
                        <>
                            {displayText}
                            <Tooltip arrow title={toolTipMessages.parcelCode} placement="top">
                                <InfoTwoToneIcon />
                            </Tooltip>
                        </>
                    );
                },
            },
        },
    ];

    return (
        <>
            <Box className="results-grid">
                <Card>
                    <CardContent className="table-card">
                        <Grid container spacing={3}>
                            <Grid container item md={12} xs={12}>
                                <Grid item md={12} xs={12}>
                                    <Box className="parcel-results-container">
                                        <Typography variant="body2">
                                            {(parcelsAreLoading || shouldShowLoading) && <LoadingIndicator message="" />}
                                            {parcels ? `Showing ${parcels.length} results` : ''}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <FmtpTable
                                    columns={columns}
                                    data={
                                        parcels?.map((parcel) => ({
                                            customer: parcel?.customer?.customerName,
                                            endpoint: parcel?.endPointInfo
                                                ? formatEndpointAddress(parcel?.endPointInfo, true)
                                                : '',
                                            containerType: parcel?.containerType,
                                            distress: parcel?.distress ? 'Yes' : 'No',
                                            possessionDate: formatDate(parcel?.possessionDateLocal),
                                            deliveryDate: formatDate(parcel?.deliveryDateLocal),
                                            deliveryLocationAlert: parcel?.deliveryLocationAlert ? 'status--danger' : '',
                                            parcelCode: parcel ?? {},
                                            sourceSystemTooltip: parcel.parcelId
                                                .map(({ id, sourceSystem }) => `${sourceSystem}: ${id}`)
                                                .join(', '),
                                        })) ?? []
                                    }
                                    fetchMoreOnBottomReached={updateFormSizeHandler}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRows}
                                    onSortChange={onSortChange}
                                    enableSorting={true}
                                    initialSortingState={[sortOrder]}
                                    containerStyles={{
                                        height: `${
                                            shouldCondenseForm
                                                ? `${TABLE_CONTAINER_HEIGHT + 6}vh`
                                                : `${TABLE_CONTAINER_HEIGHT}vh`
                                        }`,
                                    }}
                                    showLoading={false}
                                    isFetching={parcelsAreLoading || shouldShowLoading}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
}
